/**
 * Simple WebSocket Test
 * Basic test to verify connection to our ultra-fast WebSocket server
 */

import React, { useState } from 'react';

function SimpleWebSocketTest() {
    const [ws, setWs] = useState(null);
    const [connectionStatus, setConnectionStatus] = useState('❌ Not connected');
    const [connectionDetails, setConnectionDetails] = useState({});
    const [messages, setMessages] = useState([]);

    function connectWebSocket() {
        console.log('🔐 Starting WebSocket connection with proper auth flow...');
        
        const accessToken = localStorage.getItem("accessToken");
        console.log('🔐 Access Token check:', accessToken ? 'Found' : 'Not found');
        
        if (!accessToken) {
            setConnectionStatus('❌ No access token found. Please login first.');
            return;
        }

        // Step 1: Get WebSocket-specific token from auth service
        getWebSocketToken(accessToken)
            .then(wsToken => {
                if (wsToken) {
                    console.log('🎫 WebSocket token obtained:', wsToken.substring(0, 20) + '...');
                    connectWithWebSocketToken(wsToken);
                } else {
                    setConnectionStatus('❌ Failed to get WebSocket token');
                }
            })
            .catch(error => {
                console.error('❌ Error getting WebSocket token:', error);
                setConnectionStatus('❌ WebSocket token error: ' + error.message);
            });
    }

    async function getWebSocketToken(accessToken) {
        console.log('🎫 Requesting WebSocket token from auth service...');
        
        try {
            const AUTH_BASE_URL = 'http://localhost:8000';
            
            // Step 1: Get user profile (keeping existing logic)
            let profile;
            const profileEndpoints = [
                `${AUTH_BASE_URL}/api/v1/auth/me`,
                `${AUTH_BASE_URL}/api/v1/users/me`, 
                `${AUTH_BASE_URL}/api/v1/user/profile`,
                `${AUTH_BASE_URL}/auth/me`
            ];
            
            for (const endpoint of profileEndpoints) {
                console.log(`🔍 Trying profile endpoint: ${endpoint}`);
                try {
                    const profileResponse = await fetch(endpoint, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${accessToken}`,
                            'Content-Type': 'application/json'
                        }
                    });
                    
                    console.log(`📊 Response status for ${endpoint}:`, profileResponse.status);
                    
                    if (profileResponse.ok) {
                        profile = await profileResponse.json();
                        console.log('✅ Profile endpoint found:', endpoint);
                        console.log('👤 User profile:', profile);
                        break;
                    } else {
                        const errorText = await profileResponse.text();
                        console.log(`❌ ${endpoint} failed:`, profileResponse.status, errorText.substring(0, 100));
                    }
                } catch (error) {
                    console.log(`❌ ${endpoint} error:`, error.message);
                }
            }

            if (!profile) {
                console.log('🎫 No profile endpoint found, using access token directly');
                return accessToken;
            }

            // Step 2: Request WebSocket token with QUERY PARAMETERS (FastAPI format)
            const userId = profile.id || profile.user_id || 1;
            const sessionId = profile.session_id || 1;
            
            console.log(`🎫 Requesting WebSocket token for user_id: ${userId}, session_id: ${sessionId}`);
            
            // Build URL with query parameters (this is what FastAPI expects!)
            const queryParams = new URLSearchParams({
                user_id: userId.toString(),
                session_id: sessionId.toString()
            });
            
            const wsTokenEndpoint = `${AUTH_BASE_URL}/api/v1/ws-auth/issue-socket-token?${queryParams}`;
            
            console.log(`🔍 Requesting WebSocket token with query params: ${wsTokenEndpoint}`);
            
            const wsTokenResponse = await fetch(wsTokenEndpoint, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                }
                // NO BODY - FastAPI expects query parameters only
            });

            console.log(`📊 WebSocket token response status:`, wsTokenResponse.status);

            if (wsTokenResponse.ok) {
                const tokenData = await wsTokenResponse.json();
                console.log('✅ WebSocket token obtained:', tokenData);
                return tokenData.socket_token || tokenData.access_token || tokenData.token;
            } else {
                const errorText = await wsTokenResponse.text();
                console.log(`❌ WebSocket token request failed:`, wsTokenResponse.status);
                console.log(`❌ Error details:`, errorText);
                
                // Fall back to access token
                console.log('🎫 Falling back to access token');
                return accessToken;
            }
            
        } catch (error) {
            console.error('❌ Error in getWebSocketToken:', error);
            console.log('🎫 Falling back to access token due to error');
            return accessToken;
        }
    }

    function connectWithWebSocketToken(wsToken) {
        console.log('🌐 Connecting to WebSocket with dedicated token...');
        
        const wsUrl = 'ws://localhost:8080';
        console.log('🌐 WebSocket URL:', wsUrl);
        
        const newWs = new WebSocket(wsUrl);
        setWs(newWs);
        
        newWs.onopen = function(event) {
            console.log('✅ WebSocket connection opened:', event);
            setConnectionStatus('🔗 Connected - waiting for auth_required...');
            setConnectionDetails({
                url: wsUrl,
                readyState: newWs.readyState,
                protocol: newWs.protocol || 'none'
            });
        };
        
        newWs.onmessage = function(event) {
            console.log('📨 Raw message received:', event.data);
            
            let parsed;
            try {
                parsed = JSON.parse(event.data);
                console.log('📨 Parsed message:', parsed);
            } catch (e) {
                console.error('❌ Failed to parse message:', e);
                return;
            }
            
            // Handle auth_required message
            if (parsed.type === 'auth_required') {
                console.log('🔐 Server requesting authentication...');
                
                const authMessage = {
                    type: 'auth',
                    token: wsToken, // Use the WebSocket-specific token
                    timestamp: Date.now()
                };
                
                console.log('📤 Sending WebSocket auth message:', authMessage);
                newWs.send(JSON.stringify(authMessage));
                
            } else if (parsed.type === 'auth_success') {
                console.log('✅ WebSocket authentication successful!');
                setConnectionStatus('✅ Connected and authenticated!');
                
            } else if (parsed.type === 'auth_error') {
                console.error('❌ WebSocket authentication failed:', parsed);
                setConnectionStatus(`❌ Auth failed: ${parsed.message}`);
                
            } else {
                // Handle other message types
                setMessages(prev => [...prev, `📨 ${parsed.type}: ${parsed.message || JSON.stringify(parsed)}`]);
            }
        };
        
        newWs.onerror = function(error) {
            console.error('❌ WebSocket error:', error);
            setConnectionStatus('❌ WebSocket error occurred');
        };
        
        newWs.onclose = function(event) {
            console.log('🔌 WebSocket closed:', event);
            setConnectionStatus(`🔌 Connection closed: ${event.reason || 'Unknown reason'} (${event.code})`);
        };
    }

    function disconnectWebSocket() {
        if (ws && ws.readyState === WebSocket.OPEN) {
            ws.close();
            setConnectionStatus('🔌 Disconnected');
        }
    }

    function sendTestMessage() {
        if (ws && ws.readyState === WebSocket.OPEN) {
            const testMessage = {
                type: 'test',
                message: 'Hello from frontend!',
                timestamp: Date.now()
            };
            console.log('📤 Sending test message:', testMessage);
            ws.send(JSON.stringify(testMessage));
        } else {
            console.log('❌ WebSocket not connected');
        }
    }

    return (
        <div className="p-8 max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold mb-6">🔌 WebSocket Connection Test</h1>
            
            <div className="bg-gray-100 p-4 rounded-lg mb-6">
                <h2 className="text-xl font-semibold mb-2">Connection Status</h2>
                <p className="text-lg">{connectionStatus}</p>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <h2 className="text-xl font-semibold mb-2">Connection Details</h2>
                <pre className="text-sm">{JSON.stringify(connectionDetails, null, 2)}</pre>
            </div>

            <div className="flex gap-4 mb-6">
                <button 
                    onClick={connectWebSocket}
                    className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg"
                >
                    🔗 Connect
                </button>
                
                <button 
                    onClick={disconnectWebSocket}
                    className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-lg"
                >
                    🔌 Disconnect
                </button>
                
                <button 
                    onClick={sendTestMessage}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg"
                >
                    📤 Send Test Message
                </button>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
                <h2 className="text-xl font-semibold mb-2">Message Log</h2>
                <div className="max-h-64 overflow-y-auto">
                    {messages.length === 0 ? (
                        <p className="text-fg-2">No messages yet...</p>
                    ) : (
                        messages.map((msg, index) => (
                            <div key={index} className="text-sm mb-1 font-mono">
                                {msg}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}

export default SimpleWebSocketTest;
